import React, { useState, useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Dialog from '@mui/material/Dialog'
import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import { IoMdCheckmarkCircle } from 'react-icons/io'
import { IconContext } from 'react-icons'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import ConfirmationButton from '../../misc/ConfirmationButton'
import EditProfile from '../Users/User'
import Settings from '../Settings/Settings'
import Company from './Company'
import SelectContract from './SelectContract'
import { getObjects, addObject } from '../../../api/apiClientCrud'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import getStripe from '../../../services/getStripe'
import { CircularProgress } from '@mui/material'
import { EMAIL_INTERVENTION_TEMPLATE } from '../../../utils/emailTemplates/InterventionEmail'
import { EMAIL_ATTESTATION_TEMPLATE } from '../../../utils/emailTemplates/InterventionMaterialEmail'
import { EMAIL_INVOICE_TEMPLATE } from '../../../utils/emailTemplates/InvoiceEmail'
import { EMAIL_QUOTE_TEMPLATE } from '../../../utils/emailTemplates/QuoteEmail'
import { EMAIL_CREDIT_TEMPLATE } from '../../../utils/emailTemplates/CreditNoteEmail'

const stepsConfig = [
  { label: 'Votre entreprise', key: 'company', component: Company },
  { label: 'Paramètres', key: 'settings', component: Settings },
  { label: 'Votre utilisateur', key: 'user', component: EditProfile },
  { label: 'Abonnement', key: 'subscription', component: SelectContract }
]

const CreateCompany = ({ setOpen, open, ...other }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const toastId = useRef(null)

  const [activeStep, setActiveStep] = useState(0)
  const [isSubmit, setIsSubmit] = useState(false)
  const [contractSelected, setContractSelected] = useState(1)

  const [stepData, setStepData] = useState({
    company: { data: {}, errors: false, check: false, stepFailed: false },
    settings: { data: {}, errors: false, check: false, stepFailed: false },
    user: { data: {}, errors: false, check: false, stepFailed: false },
    subscription: { data: {}, errors: false }
  })

  const [modules, setModules] = useState([])

  const isStepFailed = (stepIndex) => {
    const key = stepsConfig[stepIndex].key
    return !!stepData[key]?.stepFailed
  }

  const updateStepData = (key, newData) => {
    setStepData((prev) => ({ ...prev, [key]: { ...prev[key], ...newData } }))
  }

  const resetAllSteps = () => {
    setActiveStep(0)

    const newStepData = Object.keys(stepData).reduce((acc, key) => {
      acc[key] = {
        data: {},
        errors: false,
        check: false,
        stepFailed: false
      }
      return acc
    }, {})

    setStepData(newStepData)
  }

  const cancel = () => {
    resetAllSteps()
    navigate(-1)
  }

  const handleNext = () => {
    const currentKey = stepsConfig[activeStep].key
    updateStepData(currentKey, { check: true })
    if (activeStep === stepsConfig.length - 1) setIsSubmit(true)
  }

  const handleBack = () => {
    setActiveStep((prev) => prev - 1)
  }
  //A ACTIVER APRES
  /*  useEffect(() => {
  if (activeStep === stepsConfig.length) {
    const timer = setTimeout(() => navigate('/login'), 3000)
    return () => clearTimeout(timer)
  }
}, [activeStep])*/

  useEffect(() => {
    let isMounted = true
    const fetchModules = async () => {
      try {
        const res = await getObjects('module')
        if (isMounted) setModules(res.data)
      } catch (e) {}
    }
    fetchModules()
    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    if (!isSubmit) return

    const toastError = (msg) =>
      toast.update(toastId.current, { type: 'error', render: msg, autoClose: 2000, isLoading: false })

    const submitCompanyFlow = async () => {
      toastId.current = toast.loading('Enregistrement en cours')
      try {
        const companyData = { ...stepData.company.data }
        Object.assign(companyData, {
          intervention_email_template: EMAIL_INTERVENTION_TEMPLATE,
          attestation_email_template: EMAIL_ATTESTATION_TEMPLATE,
          invoice_email_template: EMAIL_INVOICE_TEMPLATE,
          quote_email_template: EMAIL_QUOTE_TEMPLATE,
          credit_email_template: EMAIL_CREDIT_TEMPLATE
        })

        const access_rights = modules.map((m) => ({
          module_id: m.id,
          is_access: m.is_access,
          is_list: m.is_list,
          is_view: m.is_view,
          is_add: m.is_add,
          is_edit: m.is_edit,
          is_delete: m.is_delete
        }))

        const role = {
          name: 'admin',
          is_admin: true,
          is_grant_admin: true,
          access_rights
        }

        let contract = {}
        if (contractSelected !== 1) {
          contract = {
            owner_id: 1,
            contract_date: new Date(),
            contract_start_date: new Date(),
            contract_type: contractSelected,
            periodYear: dayjs(new Date()).format('YYYY'),
            periodMonth: dayjs(new Date()).format('MM'),
            periodYearMonth: dayjs(new Date()).format('YYYYMM'),
            status: 0,
            lines: []
          }

          for (const module of modules) {
            let line = { module_id: module.id, module: module.module }
            if (module.is_free_account) {
              line = { ...line, is_access: 1, is_quantity: module.is_quantity }
            }
            if (
              module.module === 'Invoices' ||
              module.module === 'Quotes' ||
              module.module === 'Interventions' ||
              module.module === 'CreditNotes'
            ) {
              line = { ...line, is_access: 1, is_quantity: module.is_quantity }
              if (contractSelected === 2) line = { ...line, quantity: 10 }
              else if (contractSelected === 3) line = { ...line, quantity: 0 }
            }
            if (module.module === 'Events' || module.module === 'Income' || module.module === 'PriceBook') {
              line = { ...line, is_quantity: module.is_quantity }
              if (contractSelected === 2) line = { ...line, is_access: 0 }
              else if (contractSelected === 3) line = { ...line, is_access: 1 }
            }
            if (module.module === 'Users' || module.module === 'Roles' || module.module === 'PriceBook') {
              line = { ...line, is_access: 1, is_quantity: module.is_quantity }
            }

            contract.lines.push(line)
          }
        }

        const payload = {
          company: {
            ...companyData,
            createdBy: stepData.user.data.email
          },
          settings: {
            ...stepData.settings.data,
            createdBy: stepData.user.data.email
          },
          role,
          user: {
            ...stepData.user.data,
            createdBy: stepData.user.data.email
          },
          customer: {
            owner_id: 1,
            customer_name: companyData.name,
            customer_type: 1,
            siret: companyData.siret,
            siren: companyData.siren,
            address1: companyData.address1,
            address2: companyData.address2,
            zip: companyData.zip,
            city: companyData.city,
            phone: companyData.phone,
            email: companyData.email
          },
          contract
        }

        const formData = new FormData()
        formData.append('data', JSON.stringify(payload))
        if (companyData.file) {
          formData.append('file', companyData.file)
        }

        // 🔥 SINGLE API CALL
        const signupRes = await addObject('signup', formData)

        if (signupRes?.data?.contract) {
          const contact = signupRes.data.customer?.email || ''
          const contract_number = signupRes.data.contract?.contract_number || ''
          const stripe = await getStripe()
          const { error } = await stripe.redirectToCheckout({
            lineItems: [
              {
                price: `${process.env.REACT_APP_STRIPE_PRICE_ID}`,
                quantity: 1
              }
            ],
            mode: 'subscription',
            successUrl: `http://localhost:3000/login`,
            cancelUrl: `http://localhost:3000/cancel`,
            customerEmail: contact,
            clientReferenceId: `${contract_number}`
          })
          if (error) {
            toastError('Erreur Stripe : ' + error.message)
          }
        }

        toast.update(toastId.current, {
          type: 'success',
          render: 'Enregistrement terminé',
          autoClose: 2000,
          isLoading: false
        })
        setActiveStep(stepsConfig.length)
      } catch (e) {
        toast.update(toastId.current, {
          type: 'error',
          render: e.response?.statusText || e.message,
          autoClose: 2000,
          isLoading: false
        })
      } finally {
        setIsSubmit(false)
      }
    }

    submitCompanyFlow()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmit])

  const setErrorsCompany = (val) => updateStepData('company', { errors: val })
  const setErrorsSettings = (val) => updateStepData('settings', { errors: val })
  const setErrorsUser = (val) => updateStepData('user', { errors: val })
  const renderStep = (stepIndex) => {
    const { key, component: Component } = stepsConfig[stepIndex]

    if (key === 'subscription') {
      return <SelectContract mode={'wizard'} setContractSelected={setContractSelected} />
    }

    if (key === 'company') {
      return (
        <Component
          modeProps='wizard'
          setStepFailed={(val) => updateStepData(key, { stepFailed: val })}
          companyAdd={stepData[key].data}
          setCompanyAdd={(data) => updateStepData(key, { data })}
          check={stepData[key].check}
          setCheck={(val) => updateStepData(key, { check: val })}
          setErrorsCompany={setErrorsCompany} // ✅ now works
          onValidStep={() => {
            setActiveStep((prev) => prev + 1)
          }}
        />
      )
    }

    if (key === 'settings') {
      return (
        <Component
          modeProps='wizard'
          setSettingsAdd={(data) => updateStepData(key, { data })}
          settingsAdd={stepData[key].data}
          errors={stepData[key].errors}
          setErrorsSettings={setErrorsSettings} // ✅ now works
          check={stepData[key].check}
          setCheck={(val) => updateStepData(key, { check: val })}
          setStepFailed={(val) => updateStepData(key, { stepFailed: val })}
          onValidStep={() => {
            setActiveStep((prev) => prev + 1)
          }}
        />
      )
    }

    if (key === 'user') {
      return (
        <Component
          modeProps='wizard'
          setUserAdd={(data) => updateStepData(key, { data })}
          userAdd={stepData[key].data}
          errors={stepData[key].errors}
          setErrorsUser={setErrorsUser} // ✅ now works
          check={stepData[key].check}
          setCheck={(val) => updateStepData(key, { check: val })}
          setStepFailed={(val) => updateStepData(key, { stepFailed: val })}
          onValidStep={() => {
            setActiveStep((prev) => prev + 1)
          }}
        />
      )
    }

    return null
  }

  return (
    <Dialog fullWidth maxWidth='lg' sx={{ '& .MuiDialog-paper': { height: 700 } }} open={true} {...other}>
      <DialogTitle>
        <Box display='flex' flexDirection='column'>
          <Box>Création Compte</Box>
          <Stepper activeStep={activeStep}>
            {stepsConfig.map((step, index) => {
              const hasError = isStepFailed(index)
              return (
                <Step key={step.label}>
                  <StepLabel
                    sx={{
                      '& .MuiStepLabel-label': {
                        color: hasError ? 'error.main' : 'inherit'
                      }
                    }}
                  >
                    {step.label}
                  </StepLabel>
                </Step>
              )
            })}
          </Stepper>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box display='flex' flexDirection='column'>
          {activeStep === stepsConfig.length ? (
            <Box display='flex' flexDirection='column' alignItems='center' justifyContent='center' pt={10}>
              <IconContext.Provider value={{ size: '42px', color: theme.palette.success.main }}>
                <IoMdCheckmarkCircle />
              </IconContext.Provider>
              <Typography>Votre compte a été créé avec succès</Typography>
              <Typography>Vous pouvez maintenant vous connecter avec vos identifiants</Typography>
            </Box>
          ) : (
            renderStep(activeStep)
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <ConfirmationButton
          message='Voulez-vous vraiment annuler?'
          color='primary'
          onClickOk={cancel}
          disabled={activeStep === stepsConfig.length - 1}
        >
          Annuler
        </ConfirmationButton>
        <Box display='flex' gap={1}>
          <Button
            color='inherit'
            disabled={activeStep === 0 || activeStep === stepsConfig.length - 1}
            onClick={handleBack}
          >
            Précédent
          </Button>
          <Button
            onClick={handleNext}
            disabled={isSubmit} // prevent double submit
            startIcon={isSubmit ? <CircularProgress size={16} /> : null}
          >
            {isSubmit
              ? 'Traitement...'
              : activeStep === stepsConfig.length - 1
              ? contractSelected === 0
                ? 'Passer'
                : 'Valider'
              : 'Suivant'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default CreateCompany
